import { DeleteOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Flex, Form, Input, Modal, notification, Row, Space, Table, TableColumnsType, Tag, Tooltip, Typography } from "antd";
import { useDeleteInvoiceNameMutation, useGetInvoiceNamesQuery } from "api/sales-order/invoice-name-api";
import { OBJ_COLOR } from "commons/constants";
import useFormToggle from "hooks/useFormToggle";
import { useEffect, useState } from "react";
import ImportExcelInvoiceNameButton from "./components/ImportExcelInvoiceNameButton";
import InvoiceNameModal from "./components/InvoiceNameModal";
import { InvoiceName } from "./types";

interface FilterForm {
	search?: string;
}

const InvoiceNamePage = () => {
	const [form] = Form.useForm<FilterForm>();
	const [modal, contextHolder] = Modal.useModal();

	const [selected, setSelected] = useState<InvoiceName | undefined>();
	const [page, setPage] = useState({ pageNumber: 1, pageSize: 20 });
	const [params, setParams] = useState<FilterForm>({});

	const { open, onOpen, onClose } = useFormToggle();

	const { data, isFetching } = useGetInvoiceNamesQuery({ ...page, ...params });

	const [deleteInvoiceName] = useDeleteInvoiceNameMutation();

	const [failed, setFailed] = useState<string[]>([]);

	useEffect(() => {
		if (!open) setSelected(undefined);
	}, [open]);

	const handleOnFinish = (values: FilterForm) => {
		setPage((prev) => ({ ...prev, pageNumber: 1 }));
		setParams(values);
	};

	const handleOnReset = () => {
		form.resetFields();
		setParams({});
		setPage({ pageNumber: 1, pageSize: 20 });
	};

	const handleOnEdit = (record: InvoiceName) => {
		setSelected(record);
		onOpen();
	};

	const handleOnDelete = (id: number) => {
		modal.confirm({
			title: "Xác nhận xoá",
			icon: <ExclamationCircleFilled />,
			content: "Bạn có chắc chắn muốn xoá Invoice Name này không?",
			okText: "Xoá",
			okType: "danger",
			cancelText: "Huỷ",
			onOk: async () => {
				try {
					await deleteInvoiceName(id).unwrap();
					notification.success({ message: "Xoá Invoice Name thành công!" });
				} catch (error: any) {
					notification.error({ message: error?.data?.message || "Có lỗi xảy ra." });
				}
			},
		});
	};

	const columns: TableColumnsType<InvoiceName> = [
		{
			title: "STT",
			dataIndex: "id",
			width: 60,
			render: (_, __, index) => (page.pageNumber - 1) * page.pageSize + index + 1,
		},
		{
			title: "Mã sản phẩm",
			dataIndex: "itemCode",
			ellipsis: true,
		},
		{
			title: "Tên sản phẩm",
			dataIndex: "itemName",
			ellipsis: true,
		},
		{
			title: "Loại gà",
			dataIndex: "chickenType",
			width: 100,
		},
		{
			title: "Giới tính",
			dataIndex: "gender",
			width: 120,
		},
		{
			title: "Tên xuất hoá đơn",
			dataIndex: "invoiceName",
			ellipsis: true,
		},
		{
			title: "Tên trên PXK",
			dataIndex: "deliveryNoteName",
			ellipsis: true,
			render: (deliveryNoteName: string | null) => deliveryNoteName || "-",
		},
		{
			title: "Trạng thái",
			dataIndex: "isActive",
			width: 130,
			render: (isActive: boolean) => <Tag color={isActive ? OBJ_COLOR.SUCCESS : OBJ_COLOR.DANGER}>{isActive ? "Hoạt động" : "Không hoạt động"}</Tag>,
		},
		{
			title: "Action",
			dataIndex: "id",
			fixed: "right",
			width: 90,
			render: (id: number, record) => (
				<Flex gap={12} align="center">
					<Tooltip title="Chỉnh sửa">
						<EditOutlined style={{ cursor: "pointer", fontSize: 18, color: OBJ_COLOR.INFO }} onClick={() => handleOnEdit(record)} />
					</Tooltip>
					<Tooltip title="Xoá">
						<DeleteOutlined style={{ cursor: "pointer", fontSize: 18, color: OBJ_COLOR.DANGER }} onClick={() => handleOnDelete(id)} />
					</Tooltip>
				</Flex>
			),
		},
	];

	return (
		<>
			{contextHolder}

			<Row gutter={[20, 20]}>
				<Col span={24}>
					<Form form={form} layout="vertical" onFinish={handleOnFinish}>
						<Row gutter={[10, 10]} align="bottom">
							<Col xs={12} xl={6}>
								<Form.Item label="Tìm kiếm" name="search">
									<Input placeholder="Mã sản phẩm, tên xuất hoá đơn, tên trên PXK..." allowClear />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button style={{ width: "100%" }} type="primary" htmlType="submit" loading={isFetching}>
										Tìm kiếm
									</Button>
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button style={{ width: "100%", background: OBJ_COLOR.WARNING }} type="primary" onClick={handleOnReset}>
										Reset
									</Button>
								</Form.Item>
							</Col>
							<Col xs={12} xl={10}>
								<Form.Item>
									<Flex justify="flex-end">
										<Space>
											<ImportExcelInvoiceNameButton disabled={isFetching} setFailed={setFailed} />
											<Button type="primary" icon={<PlusOutlined />} onClick={onOpen}>
												Thêm mới
											</Button>
										</Space>
									</Flex>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Col>

				<Col span={24}>
					{failed.length > 0 && (
						<Alert
							closable
							onClose={() => setFailed([])}
							type="error"
							showIcon
							message="Các dòng bị lỗi khi import Excel"
							description={
								<Space direction="vertical" size={4}>
									{failed.map((error, index) => (
										<Typography.Text key={index}>{error}</Typography.Text>
									))}
								</Space>
							}
						/>
					)}
				</Col>

				<Col span={24}>
					<Alert
						type="info"
						showIcon
						message="Cấu trúc file import Excel"
						description={
							<Space direction="vertical" size={4}>
								<Typography.Text>Các cột theo đúng thứ tự/header trong file:</Typography.Text>
								<Typography.Text strong>Mã sản phẩm | Tên Tồn Kho hệ thống | Loại gà | Giới tính | Tên xuất hóa đơn | Tên trên PXK</Typography.Text>
								<Typography.Text>Giới tính hợp lệ: Trống, Mái, Xô. Loại gà hợp lệ: 1, 2, 3, 4. Cột "Tên trên PXK" không bắt buộc.</Typography.Text>
							</Space>
						}
					/>
				</Col>

				<Col span={24}>
					<Table
						loading={isFetching}
						columns={columns}
						dataSource={data?.items?.map((item) => ({ ...item, key: item.id }))}
						scroll={{ x: true }}
						pagination={{
							current: page.pageNumber,
							pageSize: page.pageSize,
							total: data?.totalCount,
							showSizeChanger: true,
							pageSizeOptions: ["20", "50", "100"],
							onChange: (pageNumber, pageSize) => setPage({ pageNumber, pageSize }),
						}}
					/>
				</Col>
			</Row>

			<InvoiceNameModal open={open} onClose={onClose} invoiceName={selected} />
		</>
	);
};

export default InvoiceNamePage;
